import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { readRawUserData } from '../../services/userData';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const raw = readRawUserData();
  // Use nullish coalescing so an empty string (if ever intentionally stored) is not overridden
  const docId = raw?.docId ?? raw?.data?.docId ?? raw?.body?.data?.docId;
  const hasUserData = !!docId;

  const [redirectTarget, setRedirectTarget] = useState<string | null>(null);

  // One-time remediation effect if auth present but user-data missing
  useEffect(() => {
    if (isAuthenticated && !hasUserData && !redirectTarget) {
      try { localStorage.clear(); } catch { /* ignore */ }
      try { logout(); } catch { /* ignore */ }
      const docIdFromUrl = searchParams.get('docId');
      const target = docIdFromUrl ? `/?docId=${encodeURIComponent(docIdFromUrl)}` : '/';
      setRedirectTarget(target);
    }
  }, [isAuthenticated, hasUserData, redirectTarget, logout, searchParams]);

  // Primary unauthenticated gate (only if we are NOT in the special remediation redirect)
  if (!isAuthenticated && !redirectTarget) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
